import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {}
});

/**
 * ThemeProvider
 * Manages light / dark theme state.
 * - Defaults to system prefers-color-scheme.
 * - Applies data-theme attribute on <html> tag (<html data-theme="dark"> or <html data-theme="light">).
 * - Does NOT use localStorage / sessionStorage.
 * - Listens for system theme updates when user hasn't explicitly toggled.
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Default to light theme as requested
    return 'light';
  });

  const [hasManuallyToggled, setHasManuallyToggled] = useState(false);

  // Apply data-theme attribute to <html> element
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Listen to OS prefers-color-scheme changes if not manually set
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handleSystemThemeChange = (e) => {
      if (!hasManuallyToggled) {
        setTheme(e.matches ? 'light' : 'dark');
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [hasManuallyToggled]);

  const toggleTheme = () => {
    setHasManuallyToggled(true);
    setTheme((prevTheme) => (prevTheme === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
